import React from 'react';
import { StandardPdfTemplate } from '../../templates/StandardPdfTemplate';
import { ProjectData, ProjectInfoData } from '../../shared/types/PdfCommonTypes';
import { useTranslation } from 'react-i18next';
import { AirportResultsCore } from './sections/AirportResultsCore';

interface IAirportPdfDocumentProps {
    images: {
        tireImageUnstabilized?: string;
        tireImageIncreasedPassages?: string;
        tireImageGeogrid?: string;
        // Dynamic design sections - optional for backward compatibility
        surfaceDesignImages?: {
            canvas1?: string;
            canvas2?: string;
            canvas3?: string;
            tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
            tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
            tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
        };
        baseDesignImages?: {
            canvas1?: string;
            canvas2?: string;
            canvas3?: string;
            tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
            tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
            tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
        };
        subbaseDesignImages?: {
            canvas1?: string;
            canvas2?: string;
            canvas3?: string;
            tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
            tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
            tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
        };
    };
    projectData: {
        projectInfo?: any;
        dynamicProject?: any;
        output?: any;
        aashtoResults?: any;
    };
    // Direct props for dynamic design images (from ButtonPanel)
    surfaceDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
        tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
        tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
    };
    baseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
        tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
        tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
    };
    subbaseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: { passesFirstYear?: number; w18?: number; thickness?: number };
        tableData2?: { passesFirstYear?: number; newW18?: number; tbr?: number };
        tableData3?: { passesFirstYear?: number; w18?: number; thicknessSaving?: number };
    };
}

export const AirportResultsPdfV2: React.FC<IAirportPdfDocumentProps> = ({ images, projectData, surfaceDesignImages, baseDesignImages, subbaseDesignImages }) => {
    const { t } = useTranslation();

    // Extract project information following the actual data structure from ButtonPanel
    const dynamicProject = projectData?.dynamicProject;
    const dynamicProjectData = dynamicProject?.data;
    const projectInfoFromData = projectData?.projectInfo;

    // Extract values from dynamicProject structure
    const projectInfoData = dynamicProjectData?.projectInfo;
    const projectMapImage = dynamicProjectData?.mapImage;
    const projectType = dynamicProjectData?.type || 'airport';
    const unitSystem = dynamicProjectData?.unit || 'Metric';

    // Extract AASHTO results
    const aashtoResults = dynamicProjectData?.aashtoResults || projectData?.aashtoResults;

    // Backward compatibility with legacy structure
    const projectInfo = projectInfoFromData;

    // Prepare data for StandardPdfTemplate
    const projectDataForTemplate: ProjectData = {
        projectName: dynamicProjectData?.projectName || projectInfo?.projectName || 'N/A',
        projectType: projectType,
        unit: unitSystem,
        mapImage: projectMapImage,
        aashtoResults: aashtoResults,
    };

    const projectInfoDataForTemplate: ProjectInfoData = {
        data: {
            nrProject: projectInfoData?.data?.nrProject || projectInfo?.nrProject || projectInfo?.projectNo || 'N/A',
        },
        projectOwner: projectInfoData?.projectOwner || projectInfoData?.owner || projectInfo?.projectOwner || projectInfo?.owner || 'N/A',
        designerFirmName: projectInfoData?.designerFirmName || projectInfo?.designerFirmName || projectInfo?.designerFirm || 'N/A',
        designerFirmCountry: projectInfoData?.designerFirmCountry || projectInfo?.designerFirmCountry || 'N/A',
        designerFirmCity: projectInfoData?.data?.designerFirmCity || projectInfo?.designerFirmCity || 'N/A',
        coordinates: projectInfoData?.coordinates || projectInfo?.coordinates || [null, null],
    };

    return (
        <StandardPdfTemplate
            projectType={projectType}
            projectData={projectDataForTemplate}
            projectInfoData={projectInfoDataForTemplate}
            t={t}
            headerConfig={{
                customTitle: t('pdf_header_technical_report_groundmac') || 'GROUNDMAC - Design Technical Report',
                customProjectType: t('project_type_airport') || 'Airport modified',
                customSubtitle: t('subtitle_airport') || 'Designing AASHTO METHOD',
                showGeogridSubtitle: false,
            }}
            footerConfig={{
                customText: t('pdf_footer') || 'MACread eDesign',
                customNotice: t('pdf_footer_notice') || 'Footer notice',
            }}
            showProjectInfo={true}>
            {/* Use the shared core component for Airport Results */}
            <AirportResultsCore
                images={images}
                projectData={dynamicProjectData}
                output={projectData?.output}
                aashtoResults={aashtoResults}
                t={t}
                showInputData={true}
                showResults={true}
                surfaceDesignImages={surfaceDesignImages || images?.surfaceDesignImages}
                baseDesignImages={baseDesignImages || images?.baseDesignImages}
                subbaseDesignImages={subbaseDesignImages || images?.subbaseDesignImages}
            />
        </StandardPdfTemplate>
    );
};

export default AirportResultsPdfV2;
